import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { useConversations, useMessages, useSendMessage } from "../hooks/useConversations";
import { EmptyState, ErrorBanner, LoadingState, describeError } from "../components/States";
import { formatDateTime } from "../lib/format";
import { usePageMeta } from "../lib/seo";

function ConversationList({ activeId }: { activeId: string | undefined }) {
  const { data, isLoading, isError, error, refetch } = useConversations();
  const { principal } = useAuthPrincipal();

  if (isLoading) return <LoadingState label="Loading conversations…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (!data || data.items.length === 0) {
    return (
      <EmptyState title="No messages yet">
        <p>Questions about a listing? Open it and tap &quot;Message seller&quot; to start a thread.</p>
      </EmptyState>
    );
  }

  return (
    <div className="card" style={{ padding: 0 }}>
      {data.items.map((c) => {
        const isBuyer = principal?.userId === c.buyerId;
        const counterparty = isBuyer ? (c.sellerUsername ?? "Seller") : (c.buyerUsername ?? "Buyer");
        return (
          <Link
            key={c.id}
            to={`/messages/${c.id}`}
            className={`conversation-list-item ${c.id === activeId ? "is-active" : ""}`}
          >
            <div className="row row--between">
              <strong>{counterparty}</strong>
              <span className="text-sm muted">{formatDateTime(c.lastMessageAt)}</span>
            </div>
            <p className="text-sm muted">{c.listingTitle}</p>
          </Link>
        );
      })}
    </div>
  );
}

function ConversationThread({ conversationId }: { conversationId: string }) {
  const { principal } = useAuthPrincipal();
  const { data, isLoading, isError, error, refetch } = useMessages(conversationId);
  const { data: conversations } = useConversations();
  const sendMessage = useSendMessage(conversationId);
  const [body, setBody] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);

  const conversation = conversations?.items.find((c) => c.id === conversationId);

  async function handleSend(e: FormEvent) {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    setSendError(null);
    try {
      await sendMessage.mutateAsync(trimmed);
      setBody("");
    } catch (err) {
      setSendError(describeError(err));
    }
  }

  if (isLoading) return <LoadingState label="Loading conversation…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;

  return (
    <div className="card stack" style={{ padding: 0 }}>
      {conversation && (
        <div style={{ padding: "var(--space-3) var(--space-4)", borderBottom: "1px solid var(--color-border)" }}>
          <strong>{conversation.listingTitle}</strong>
          <p className="text-sm muted">
            {principal?.userId === conversation.buyerId
              ? `Seller: ${conversation.sellerUsername ?? "—"}`
              : `Buyer: ${conversation.buyerUsername ?? "—"}`}
          </p>
        </div>
      )}

      <div className="message-thread">
        {data && data.items.length === 0 && (
          <p className="text-sm muted">No messages yet — say hello.</p>
        )}
        {data?.items.map((m) => (
          <div
            key={m.id}
            className={`message-bubble ${m.senderId === principal?.userId ? "is-mine" : ""}`}
          >
            <p className="text-sm">{m.body}</p>
            <p className="text-sm muted">{formatDateTime(m.createdAt)}</p>
          </div>
        ))}
      </div>

      <form
        onSubmit={(e) => void handleSend(e)}
        className="row"
        style={{ padding: "var(--space-3) var(--space-4)", borderTop: "1px solid var(--color-border)" }}
      >
        <input
          type="text"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write a message…"
          maxLength={2000}
          style={{ flex: 1 }}
        />
        <button type="submit" className="btn btn-primary" disabled={!body.trim() || sendMessage.isPending}>
          {sendMessage.isPending ? "Sending…" : "Send"}
        </button>
      </form>
      {sendError && (
        <p className="field-error-text" role="alert" style={{ padding: "0 var(--space-4) var(--space-3)" }}>
          {sendError}
        </p>
      )}
    </div>
  );
}

export function MessagesPage() {
  const { conversationId } = useParams<{ conversationId: string }>();

  usePageMeta({
    title: "Messages",
    description: "Your buyer/seller conversations.",
    path: "/messages",
    index: false,
  });

  return (
    <div className="stack">
      <h1>Messages</h1>
      <div className="messages-layout">
        <ConversationList activeId={conversationId} />
        {conversationId ? (
          <ConversationThread conversationId={conversationId} />
        ) : (
          <div className="card">
            <p className="muted">Select a conversation to view it.</p>
          </div>
        )}
      </div>
    </div>
  );
}
